import { useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { ImportStep, SolveProblemsStep } from 'bom/enums';
import { useBomPartsImportContext } from 'bom/states';
import { Steps } from 'core/components';
import { StepsProvider, useNotificationContext } from 'core/states';

import FileImport from './FileImport';
import FileUpload from './FileUpload';
import { ImportFailedRowsModal } from './ImportFailedRowsModal';
import { PreviewImport } from './PreviewImport';
import RowMapping from './RowMapping';
import { SolveProblems } from './SolveProblems';

interface IProps {
  isOpen: boolean;
  onClose: () => void;
  fileId?: string;
}

const BomPartsImport = ({ isOpen, onClose, fileId }: IProps) => {
  const { t } = useTranslation();

  const { clearDrawerNotification } = useNotificationContext();
  const {
    file,
    onCreateImport,
    onConfirmMapping,
    problems,
    onImportParts,
    clearData,
    isLoadingNextStep,
    completedImportId,
    clearCompletedImport,
  } = useBomPartsImportContext();

  useEffect(() => {
    if (fileId) {
      onCreateImport(fileId);
    }
  }, [fileId, onCreateImport]);

  const handleOnClose = () => {
    clearDrawerNotification();
    clearData();
    onClose();
  };

  const steps = useMemo(
    () =>
      [
        {
          type: ImportStep.UPLOAD_FILE,
          title: t('titleUploadFile'),
          content: <FileUpload />,
          previousButton: {
            text: t('buttonLabelPrevious'),
          },
          nextButton: {
            hide: true,
          },
          cancelButton: {
            text: t('buttonLabelCancelImport'),
          },
        },
        {
          type: ImportStep.IMPORT_FILE,
          title: t('titleImportFile'),
          content: <FileImport />,
          previousButton: {
            text: t('buttonLabelPrevious'),
            hide: true,
          },
          nextButton: {
            text: t('buttonLabelImport'),
            options: { onClick: () => onCreateImport(), disabled: !file },
          },
          cancelButton: {
            text: t('buttonLabelCancelImport'),
          },
        },
        {
          type: ImportStep.MAPPING,
          title: t('titleMapping'),
          content: <RowMapping />,
          previousButton: {
            text: t('buttonLabelPrevious'),
          },
          nextButton: {
            text: t('buttonLabelConfirmMapping'),
            options: { onClick: onConfirmMapping, disabled: isLoadingNextStep },
          },
          cancelButton: {
            text: t('buttonLabelCancelImport'),
          },
        },
        {
          type: ImportStep.SOLVE_PROBLEMS,
          title: t('titleSolveProblems'),
          content: <SolveProblems />,
          previousButton: {
            text: t('buttonLabelPrevious'),
          },
          nextButton: {
            options: {
              disabled:
                problems?.[SolveProblemsStep.UNKNOWN_MANUFACTURERS] === null
                  ? false
                  : // A null key = its validation hasn't seeded counts yet → treat as unsolved and
                    // block Next (never let `total + undefined === NaN` slip past the `> 0` check).
                    Object.values(problems).some((problem) => problem === null) ||
                    Object.values(problems).reduce(
                      (total, problem) => total + (problem?.unsolved ?? 0),
                      0
                    ) > 0,
            },
          },
          cancelButton: {
            text: t('buttonLabelCancelImport'),
          },
        },
        {
          type: ImportStep.PREVIEW,
          title: t('titlePreviewImport'),
          content: <PreviewImport />,
          previousButton: {
            text: t('buttonLabelPrevious'),
            options: { stepType: ImportStep.MAPPING, disabled: isLoadingNextStep },
          },
          nextButton: {
            text: t('buttonLabelImportParts'),
            options: { onClick: onImportParts, disabled: isLoadingNextStep },
          },
          cancelButton: {
            text: t('buttonLabelCancelImport'),
            options: { disabled: isLoadingNextStep },
          },
        },
      ].slice(fileId ? 2 : 0),
    [t, file, onConfirmMapping, isLoadingNextStep, problems, onImportParts, fileId, onCreateImport]
  );

  return (
    <>
      <StepsProvider steps={steps} onClose={handleOnClose}>
        <Steps title={t('titleImportBillOfMaterials')} isOpen={isOpen} />
      </StepsProvider>

      <ImportFailedRowsModal importId={completedImportId} onClose={clearCompletedImport} />
    </>
  );
};

export default BomPartsImport;
